import type { TierLimits } from '../types/tier-limits';
import { toBcp47Locale, type SupportedLocale } from '../locales/locale';

type TranslateFn = (key: string) => string;

export type TierPlanFeatureKind = 'projects' | 'documents' | 'resources' | 'storage' | 'credits' | 'history';

export const FREE_TIER_FEATURE_ORDER: TierPlanFeatureKind[] = [
  'projects',
  'documents',
  'resources',
  'storage',
  'credits',
  'history',
];

export const PRO_TIER_FEATURE_ORDER: TierPlanFeatureKind[] = [
  'projects',
  'documents',
  'resources',
  'storage',
  'credits',
  'history',
];

export function formatStorageMb(mb: number): string {
  if (mb >= 1024 && mb % 1024 === 0) {
    return `${mb / 1024}GB`;
  }

  return `${mb}MB`;
}

function formatCount(count: number, locale: SupportedLocale): string {
  return count.toLocaleString(toBcp47Locale(locale));
}

function formatCountLabel(
  count: number,
  singularKey: string,
  pluralKey: string,
  t: TranslateFn,
): string {
  if (count === 1) {
    return t(singularKey).replace('{count}', '1');
  }

  return t(pluralKey).replace('{count}', String(count));
}

function formatProjectsLimit(maxProjects: number | null, t: TranslateFn): string {
  if (maxProjects === null) {
    return t('feature_unlimited_projects');
  }

  return formatCountLabel(maxProjects, 'pricing_limit_project', 'pricing_limit_projects', t);
}

function formatDocumentsLimit(maxDocumentsPerProject: number | null, t: TranslateFn): string {
  if (maxDocumentsPerProject === null) {
    return t('feature_unlimited_documents');
  }

  return formatCountLabel(
    maxDocumentsPerProject,
    'pricing_limit_document_per_project',
    'pricing_limit_documents_per_project',
    t,
  );
}

function formatResourcesLimit(maxResourcesPerProject: number | null, t: TranslateFn): string {
  if (maxResourcesPerProject === null) {
    return t('feature_unlimited_resources');
  }

  return formatCountLabel(
    maxResourcesPerProject,
    'pricing_limit_resource_per_project',
    'pricing_limit_resources_per_project',
    t,
  );
}

function formatStorageLimit(storageQuotaMB: number, t: TranslateFn): string {
  return t('pricing_limit_storage').replace('{size}', formatStorageMb(storageQuotaMB));
}

function formatCreditsLimit(limits: TierLimits, t: TranslateFn, locale: SupportedLocale): string {
  if (limits.monthlyCreditsRefill > 0) {
    return t('pricing_limit_monthly_credits').replace(
      '{count}',
      formatCount(limits.monthlyCreditsRefill, locale),
    );
  }

  return t('pricing_limit_initial_credits').replace(
    '{count}',
    formatCount(limits.initialCredits, locale),
  );
}

function formatHistoryLimit(versionHistoryDays: number | null, t: TranslateFn): string {
  if (versionHistoryDays === null) {
    return t('feature_full_history');
  }

  return t('pricing_limit_version_history_days').replace('{days}', String(versionHistoryDays));
}

export interface TierPlanFeatureItem {
  kind: TierPlanFeatureKind;
  name: string;
  tooltip?: string;
}

function formatFeatureTooltip(
  kind: TierPlanFeatureKind,
  limits: TierLimits,
  t: TranslateFn,
  locale: SupportedLocale,
): string | undefined {
  switch (kind) {
    case 'resources':
      if (limits.maxResourcesPerProject === null) {
        return t('tooltip_unlimited_resources');
      }
      return t('tooltip_limited_resources').replace(
        '{count}',
        String(limits.maxResourcesPerProject),
      );
    case 'storage':
      if (limits.storageQuotaMB >= 1024) {
        return t('tooltip_large_storage');
      }
      return t('tooltip_storage_upload_limit').replace(
        '{size}',
        formatStorageMb(limits.storageQuotaMB),
      );
    case 'credits': {
      const credits = limits.monthlyCreditsRefill > 0
        ? limits.monthlyCreditsRefill
        : limits.initialCredits;
      return t('tooltip_credits_usage')
        .replace('{count}', formatCount(credits, locale))
        .replace('{words}', formatCount(credits * 100, locale));
    }
    case 'history':
      if (limits.versionHistoryDays === null) {
        return t('tooltip_full_history');
      }
      return t('tooltip_version_history_days').replace(
        '{days}',
        String(limits.versionHistoryDays),
      );
    default:
      return undefined;
  }
}

export function getHistoryPlanTooltip(
  limits: TierLimits,
  t: TranslateFn,
  locale: SupportedLocale,
): string | undefined {
  return formatFeatureTooltip('history', limits, t, locale);
}

function buildTierPlanFeatureItems(
  limits: TierLimits,
  order: TierPlanFeatureKind[],
  t: TranslateFn,
  locale: SupportedLocale,
): TierPlanFeatureItem[] {
  const formatters: Record<TierPlanFeatureKind, () => string> = {
    projects: () => formatProjectsLimit(limits.maxProjects, t),
    documents: () => formatDocumentsLimit(limits.maxDocumentsPerProject, t),
    resources: () => formatResourcesLimit(limits.maxResourcesPerProject, t),
    storage: () => formatStorageLimit(limits.storageQuotaMB, t),
    credits: () => formatCreditsLimit(limits, t, locale),
    history: () => formatHistoryLimit(limits.versionHistoryDays, t),
  };

  return order.map((kind) => {
    const tooltip = formatFeatureTooltip(kind, limits, t, locale);
    return {
      kind,
      name: formatters[kind](),
      ...(tooltip ? { tooltip } : {}),
    };
  });
}

export function getTierPlanFeatures(
  limits: TierLimits,
  order: TierPlanFeatureKind[],
  t: TranslateFn,
  locale: SupportedLocale,
): string[] {
  return buildTierPlanFeatureItems(limits, order, t, locale).map((feature) => feature.name);
}

export function getTierPlanFeatureItems(
  limits: TierLimits,
  order: TierPlanFeatureKind[],
  t: TranslateFn,
  locale: SupportedLocale,
): TierPlanFeatureItem[] {
  return buildTierPlanFeatureItems(limits, order, t, locale);
}
